import { useEffect, useState } from "react";
import { ThumbsUp, ChatCircle, ShareFat, Heart, PaperPlaneTilt, BookmarkSimple, MusicNotes, Globe, Copy, ImageSquare } from "@phosphor-icons/react";
import { NETWORKS, NetworkDot } from "./socialShared";

const cut = (t, n) => (t.length > n ? { shown: t.slice(0, n).trimEnd(), more: true } : { shown: t, more: false });

function Avatar({ name, size = 36 }) {
  return (
    <div className="flex shrink-0 items-center justify-center rounded-full bg-zinc-800 font-semibold text-white" style={{ width: size, height: size, fontSize: size / 2.6 }}>
      {(name || "?").slice(0, 1).toUpperCase()}
    </div>
  );
}

function Media({ media, ratio = "aspect-[1.91/1]", cover = true }) {
  const m = media[0];
  if (!m) return null;
  return (
    <div className={`relative w-full overflow-hidden bg-zinc-100 ${ratio}`}>
      {m.kind === "video"
        ? <video src={m.url} className={`h-full w-full ${cover ? "object-cover" : "object-contain"}`} muted />
        : <img src={m.url} alt="" className={`h-full w-full ${cover ? "object-cover" : "object-contain"}`} />}
      {media.length > 1 && <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[10px] text-white"><Copy size={10} />1/{media.length}</span>}
    </div>
  );
}

function Text({ text, limit, moreLabel, className = "" }) {
  const { shown, more } = cut(text, limit);
  return <p className={`whitespace-pre-line break-words ${className}`}>{shown}{more && <span className="text-zinc-500">… {moreLabel}</span>}</p>;
}

function LinkedInPreview({ name, text, media }) {
  return (
    <div className="rounded-lg border bg-white text-[13px] text-zinc-900" data-testid="preview-linkedin">
      <div className="flex gap-2 p-3">
        <Avatar name={name} size={40} />
        <div><div className="font-semibold leading-tight">{name}</div><div className="text-[11px] text-zinc-500">Page entreprise</div><div className="flex items-center gap-1 text-[11px] text-zinc-500">Maintenant · <Globe size={10} /></div></div>
      </div>
      {text && <Text text={text} limit={210} moreLabel="voir plus" className="px-3 pb-2" />}
      <Media media={media} />
      <div className="flex justify-around border-t px-2 py-1.5 text-[12px] font-medium text-zinc-600">
        <span className="flex items-center gap-1"><ThumbsUp size={15} />J'aime</span><span className="flex items-center gap-1"><ChatCircle size={15} />Commenter</span><span className="flex items-center gap-1"><ShareFat size={15} />Republier</span>
      </div>
    </div>
  );
}

function FacebookPreview({ name, text, media }) {
  return (
    <div className="rounded-lg border bg-white text-[13px] text-zinc-900" data-testid="preview-facebook">
      <div className="flex gap-2 p-3">
        <Avatar name={name} />
        <div><div className="font-semibold leading-tight">{name}</div><div className="flex items-center gap-1 text-[11px] text-zinc-500">À l'instant · <Globe size={10} /></div></div>
      </div>
      {text && <Text text={text} limit={480} moreLabel="En voir plus" className={`px-3 pb-2 ${!media.length && text.length < 85 ? "text-xl" : ""}`} />}
      <Media media={media} ratio="aspect-square" />
      <div className="flex justify-around border-t px-2 py-1.5 text-[12px] font-medium text-zinc-600">
        <span className="flex items-center gap-1"><ThumbsUp size={15} />J'aime</span><span className="flex items-center gap-1"><ChatCircle size={15} />Commenter</span><span className="flex items-center gap-1"><ShareFat size={15} />Partager</span>
      </div>
    </div>
  );
}

function InstagramPreview({ name, text, media }) {
  const handle = (name || "votre_compte").toLowerCase().replace(/[^a-z0-9._]/g, "");
  return (
    <div className="rounded-lg border bg-white text-[13px] text-zinc-900" data-testid="preview-instagram">
      <div className="flex items-center gap-2 p-2.5"><Avatar name={name} size={30} /><span className="font-semibold">{handle}</span></div>
      {media.length ? <Media media={media} ratio={media[0].kind === "video" ? "aspect-[4/5]" : "aspect-square"} />
        : <div className="flex aspect-square items-center justify-center bg-zinc-100 text-xs text-red-600"><ImageSquare size={18} className="mr-1" />Image ou vidéo obligatoire</div>}
      <div className="flex gap-3 px-3 pt-2"><Heart size={20} /><ChatCircle size={20} /><PaperPlaneTilt size={20} /><BookmarkSimple size={20} className="ml-auto" /></div>
      <div className="px-3 pb-3 pt-1"><Text text={`${handle} ${text}`} limit={125 + handle.length} moreLabel="plus" /></div>
    </div>
  );
}

function TikTokPreview({ name, text, media }) {
  const m = media[0];
  return (
    <div className="relative mx-auto aspect-[9/16] w-full max-w-[260px] overflow-hidden rounded-2xl bg-black text-white" data-testid="preview-tiktok">
      {m ? (m.kind === "video" ? <video src={m.url} className="h-full w-full object-cover" muted /> : <img src={m.url} alt="" className="h-full w-full object-cover" />)
        : <div className="flex h-full items-center justify-center text-xs text-red-400">Vidéo ou photos obligatoires</div>}
      <div className="absolute bottom-16 right-2 flex flex-col items-center gap-3 text-[10px]">
        <Avatar name={name} size={34} /><Heart size={26} weight="fill" /><ChatCircle size={26} weight="fill" /><ShareFat size={26} weight="fill" />
      </div>
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3 pr-12 text-[12px]">
        <div className="font-semibold">@{(name || "compte").toLowerCase().replace(/\s+/g, "")}</div>
        <Text text={text} limit={90} moreLabel="plus" className="mt-1" />
        <div className="mt-1 flex items-center gap-1 text-[10px]"><MusicNotes size={11} />Son original</div>
      </div>
    </div>
  );
}

const RENDER = { linkedin: LinkedInPreview, facebook: FacebookPreview, instagram: InstagramPreview, tiktok: TikTokPreview };

export function PostPreview({ form, media, accounts }) {
  const [net, setNet] = useState(form.networks[0]);
  useEffect(() => { if (!form.networks.includes(net)) { setNet(form.networks[0]); } }, [form.networks, net]);
  if (!form.networks.length) {
    return <div className="flex h-full min-h-[200px] items-center justify-center rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground" data-testid="preview-empty">Choisissez un réseau pour voir l'aperçu.</div>;
  }
  const acc = accounts.find((a) => a.provider === NETWORKS[net]?.provider);
  const name = acc?.account_name || "Votre page";
  const Render = RENDER[net] || LinkedInPreview;
  const text = ((form.texts || {})[net] || form.text || "").trim();
  return (
    <div className="space-y-3" data-testid="post-preview">
      <div className="flex flex-wrap gap-1">
        {form.networks.map((n) => (
          <button key={n} type="button" onClick={() => setNet(n)} data-testid={`preview-tab-${n}`}
            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs ${net === n ? "border-foreground bg-foreground text-background" : "hover:bg-muted"}`}>
            <NetworkDot id={n} size={12} muted={net === n} />{NETWORKS[n].label}
          </button>
        ))}
      </div>
      <Render name={name} text={text} media={media} />
      <p className="text-[11px] text-muted-foreground">Aperçu indicatif : le rendu exact dépend de l'appareil et de l'application.</p>
    </div>
  );
}
